import type { AssignmentKind, MyTurfAssignmentDto, TodayAssignmentDto } from '../types/canvasser'
import { buildWalkStops, stateRuleFor, walkProgress } from './walkStops'

export type TodayUnit = 'doors' | 'stops' | 'calls'

export interface TodayCard {
  key: string
  kind: AssignmentKind
  label: string
  done: number
  total: number
  unit: TodayUnit
  turfId: string | null
  callBatchId: string | null
}

export const turfAssignmentKey = (turfId: string): string => `turf:${turfId}`

/** 'turf:<id>' -> id; anything else (call:<id>, null, garbage) -> null. */
export function turfIdOfKey(key: string | null): string | null {
  return key && key.startsWith('turf:') && key.length > 5 ? key.slice(5) : null
}

function unitOf(kind: AssignmentKind): TodayUnit {
  return kind === 'ActionRoute' ? 'stops' : kind === 'CallBatch' ? 'calls' : 'doors'
}

const EMPTY: ReadonlySet<string> = new Set()

function turfCard(
  turf: MyTurfAssignmentDto, doneHere: ReadonlyMap<string, ReadonlySet<string>>, label: string,
): TodayCard {
  const kind: AssignmentKind = turf.assignmentKind ?? 'Canvass'
  // walkStops is the only status source: server done/total are never used for turf cards.
  const { done, total } = walkProgress(
    buildWalkStops(turf.contacts, turf.addressMode, doneHere.get(turf.turfId) ?? EMPTY, stateRuleFor(turf.assignmentKind)))
  return {
    key: turfAssignmentKey(turf.turfId), kind, label, done, total, unit: unitOf(kind),
    turfId: turf.turfId, callBatchId: null,
  }
}

const fallbackLabel = (t: MyTurfAssignmentDto) => `${t.kindLabel ?? 'Canvass'} · ${t.turfLabel}`

/**
 * Today's cards. The server list orders and labels; the phone's cached turfs decide what can open
 * (a turf-backed card exists only for a turf the phone holds). Server null = offline/failed:
 * derive everything from the turfs.
 */
export function todayCards(
  server: TodayAssignmentDto[] | null,
  turfs: MyTurfAssignmentDto[],
  doneHere: ReadonlyMap<string, ReadonlySet<string>>,
): TodayCard[] {
  if (!server) return turfs.map(t => turfCard(t, doneHere, fallbackLabel(t)))
  const byId = new Map(turfs.map(t => [t.turfId, t]))
  const used = new Set<string>()
  const out: TodayCard[] = []
  for (const s of server) {
    if (s.kind === 'CallBatch') {
      out.push({
        key: s.assignmentKey, kind: s.kind, label: s.label, done: s.done, total: s.total, unit: 'calls',
        turfId: null, callBatchId: s.callBatchId ?? null,
      })
      continue
    }
    // Only CallBatch entries are turf-less; any other entry without a turf has nothing to open.
    if (!s.turfId) continue
    const turf = byId.get(s.turfId)
    if (!turf || used.has(turf.turfId)) continue
    used.add(turf.turfId)
    out.push(turfCard(turf, doneHere, s.label))
  }
  for (const t of turfs) if (!used.has(t.turfId)) out.push(turfCard(t, doneHere, fallbackLabel(t)))
  return out
}

/** The cards the home can open today: turf-backed cards, and (CF-1 P4) call batches with a batch id.
 *  The home, the Today picker and the deep links all count with this, so they agree. */
export function openableCards(cards: TodayCard[]): TodayCard[] {
  return cards.filter(c => (c.kind === 'CallBatch' ? !!c.callBatchId : !!c.turfId))
}
